/**
 * @author Muhammad Fahad
 * @datetime April, 13th 2022
 * @description: This is add building details that is responsible for adding
 *               and editing new building - this component also contains form for building edit
 */
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Subscription, Subject, Observable, of, forkJoin } from 'rxjs';
import { BuildingManagementService } from 'src/app/building-managment/building-management.service';
import { SiteManagementService } from 'src/app/site-management/site-management.service';
import { SiteManagerComponent } from 'src/app/site-management/site-manager/site-manager.component';
import { FloorManagementService } from 'src/app/floor-management/floor-management.service';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { PageName } from 'src/app/shared/utils/user-roles';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { Building, IBuilding } from '../building-management-interfaces';
import { Utility } from 'src/app/shared/utils/utility.service';
import { ConfirmDialogComponent } from 'src/app/shared-layout/Components/confirm-dialog/confirm-dialog.component';
import { HelperService } from 'src/app/shared/utils/helper.service';
import { MFValidators } from 'src/app/shared/validators/mf-validators';
import { NotificationService } from 'src/app/shared/errors/notification.service';
import { CommonService } from 'src/app/shared/utils/common.service';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  mergeMap,
  switchMap,
} from 'rxjs/operators';
import { ConfirmRemoveDialogComponent } from 'src/app/shared-layout/Components/confirm-remove-dialog/confirm-remove-dialog.component';
import { RestrictRemoveDialogComponent } from 'src/app/shared-layout/Components/restrict-remove-dialog/restrict-remove-dialog.component';
import {
  Floor,
  IFloor,
} from 'src/app/floor-management/floor-management-interface';
import { StateManagementService } from 'src/app/shared/services/state-management.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-add-building-details',
  templateUrl: './add-building-details.component.html',
  styleUrls: ['./add-building-details.component.scss'],
})
export class AddBuildingDetailsComponent implements OnInit, OnDestroy {
  site: any;
  formGroup: any;
  file: any;
  buildingPhotoKey: string | undefined;
  buildingPhotoURL: string | null | undefined;
  buildingId!: string;
  sharedData!: any;
  subscription: Subscription | undefined;
  floorimage = 'assets/svg/ico-floors-circle.svg';
  building: any = null;
  floors: any;
  editMode = false;
  readonly LANG_CONST = LANG_CONST;
  PageName = PageName;
  CONSTANTS = CONSTANTS;
  previousData: IBuilding | undefined;
  filteredSuggestions: Observable<string[]> | undefined;

  public ngDestroyed$ = new Subject();
  @Input() public selectedSite: any;
  @Input() public resetNavigation: any;
  @Input() public navigateToEditSite: any;
  @Input() navigateToAddBuilding: any;
  @Input() navigateToAddFloor: any;
  @Input() public enableBackOperation!: boolean | true;
  @Input() selectedBuilding: any;
  @Input() navigateConfirmDialog: any;
  @Input() closeConfirmDialog: any;
  @Input() result: any;
  errorMessage: string | undefined;

  /**
   * @description constructor injects all necesary services
   * @param { MatDialogRef<SiteManagerComponent> } dialogRef
   * @param { BuildingManagementService } buildingManagementService
   * @param { SiteManagementService } siteManagementService
   * @param { FloorManagementService } floorManagementService
   * @param { SharedDataService } sharedDataService
   */
  constructor(
    public dialogRef: MatDialogRef<SiteManagerComponent>,
    private buildingManagementService: BuildingManagementService,
    private siteManagementService: SiteManagementService,
    private floorManagementService: FloorManagementService,
    private sharedDataService: SharedDataService,
    private helperService: HelperService,
    private changeDetectorRef: ChangeDetectorRef,
    private notificationService: NotificationService,
    private commonService: CommonService,
    private stateManagementService: StateManagementService,
    private translate: TranslateService
  ) {}

  /**
   * @description this is onInit life cycle method
   */
  ngOnInit(): void {
    this.formGroup = new FormGroup({
      name: new FormControl(
        null,
        MFValidators.compose([
          MFValidators.required,
          MFValidators.minLength(1),
          MFValidators.maxLength(64),
          MFValidators.whitespace,
          MFValidators.buildingName,
        ])
      ),
      address: new FormControl(
        null,
        MFValidators.compose([
          MFValidators.required,
          MFValidators.minLength(2),
          MFValidators.maxLength(255),
          MFValidators.whitespace,
          MFValidators.addressPattern,
        ])
      ),
    });

    this.sharedDataService.currentData.subscribe((sharedData: any) => {
      this.sharedData = sharedData;
    });

    if (this.sharedData?.editMode?.building) {
      this.floors = [];
      this.buildingManagementService
        .getAllBuildingsBySiteId(this.selectedSite?.siteId)
        .subscribe({
          next: (buildings: Building) => {
            this.building =
              buildings &&
              buildings.find(
                (building: any) =>
                  building.buildingId ==
                  this.sharedData.selectedBuilding.buildingId
              );
            this.formGroup.patchValue({
              name: this.building?.buildingName,
              address: this.building?.buildingAddress,
              photo: this.building?.buildingImg
                ? this.building?.buildingImgName
                : '',
            });
            this.buildingPhotoURL = this.building?.buildingImgUrl;
            this.previousData = {
              buildingId: this.building?.buildingId,
              buildingName: this.building?.buildingName,
              address: this.building?.buildingAddress,
              buildingImg: this.building?.buildingImg,
              buildingImgName: this.building?.buildingImgName,
            };
          },
          error: (err: Error) => {
            if (window.__dev__) console.log('getAllBuildingsBySiteId', err);
            this.notificationService.showError(err?.message);
          },
        });
      this.getFloorBySiteBuildingId(
        this.sharedData.selectedSite.siteId,
        this.sharedData.selectedBuilding.buildingId
      );
    }

    //fetching address suggestions
    this.filteredSuggestions = this.formGroup.get('address').valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((value: string) =>
        value.length > 0
          ? this.buildingManagementService.getAddressSuggestions(value, true)
          : of([])
      )
    );
  }

  /**
   * @description this method closes the error message alert.
   */
  closeError(): void {
    this.errorMessage = undefined;
  }

  /**
   * @description this is life cycle method that works on destroy
   */
  ngOnDestroy(): void {
    this.ngDestroyed$.next(void 0);
    this.ngDestroyed$.complete();
  }

  /**
   * @description this method closes dialog
   */
  closeDialog() {
    let buildingName = this.formGroup?.controls?.name?.value;
    let buildingAddress = this.formGroup?.controls?.address?.value;
    let buildingImg = this.buildingPhotoKey ?? this.previousData?.buildingImg;
    let buildingImgName =
      this.file?.data?.name ?? this.previousData?.buildingImgName;

    let currentData: IBuilding = {
      buildingId: this.previousData?.buildingId || '',
      buildingName: buildingName,
      address: buildingAddress,
      buildingImg: buildingImg,
      buildingImgName: buildingImgName,
    };

    if (
      (this.sharedData.editMode.building &&
        Utility.compareJson(this.previousData || {}, currentData || {})) ||
      (!this.sharedData.editMode.building &&
        this.validateEmpty(buildingName, buildingAddress, buildingImg))
    ) {
      this.sharedDataService.setEditMode({
        building: false,
        floor: false,
      });
      this.resetNavigation();
      this.dialogRef.close(this.result);
      return;
    }

    this.navigateConfirmDialog();
    const dialogInstance = this.helperService.openModalDialog(
      ConfirmDialogComponent,
      {
        title: this.translate.instant('TITLE'),
        headingText: this.translate.instant('HEADING_TEXT'),
        bodyText: this.translate.instant('BODY_TEXT'),
        footerText: this.translate.instant('FOOTER_TEXT'),
      },
      CONSTANTS.FIXED_SCREEN_MODAL
    );
    dialogInstance.afterClosed().subscribe((result: boolean) => {
      this.closeConfirmDialog();
      if (result) {
        this.sharedDataService.setEditMode({
          building: false,
          floor: false,
        });
        this.resetNavigation();
        this.dialogRef.close(this.result);
      }
    });
  }

  /**
   * @description this method navigate back to the previous screen
   */
  navigateBack() {
    let buildingName = this.formGroup?.controls?.name?.value;
    let buildingAddress = this.formGroup?.controls?.address?.value;
    let buildingImg = this.buildingPhotoKey ?? this.previousData?.buildingImg;
    let buildingImgName =
      this.file?.data?.name ?? this.previousData?.buildingImgName;

    let currentData: IBuilding = {
      buildingId: this.previousData?.buildingId || '',
      buildingName: buildingName,
      address: buildingAddress,
      buildingImg: buildingImg,
      buildingImgName: buildingImgName,
    };

    if (
      (this.sharedData.editMode.building &&
        Utility.compareJson(this.previousData || {}, currentData || {})) ||
      (!this.sharedData.editMode.building &&
        this.validateEmpty(buildingName, buildingAddress, buildingImg))
    ) {
      this.sharedDataService.setSelectedBuilding({
        buildingId: this.building?.buildingId,
        buildingName: this.building?.buildingName,
      });
      this.navigateToEditSite();
      return;
    }

    this.navigateConfirmDialog();
    const dialogInstance = this.helperService.openModalDialog(
      ConfirmDialogComponent,
      {
        title: this.translate.instant('TITLE'),
        headingText: this.translate.instant('HEADING_TEXT'),
        bodyText: this.translate.instant('BODY_TEXT'),
        footerText: this.translate.instant('FOOTER_TEXT'),
      },
      CONSTANTS.FIXED_SCREEN_MODAL
    );
    dialogInstance.afterClosed().subscribe((result: boolean) => {
      this.closeConfirmDialog();
      if (result) {
        this.sharedDataService.setSelectedBuilding({
          buildingId: this.building?.buildingId,
          buildingName: this.building?.buildingName,
        });
        this.navigateToEditSite();
      }
    });
  }

  /**
   * @description this method gets file and add file name to the form
   *              also this method also starts file upload process
   */
  addPhotoClickHandler() {
    const fileUpload: any = document.getElementById(
      'fileUploadBuilding'
    ) as HTMLInputElement;
    fileUpload.onchange = () => {
      const file = fileUpload?.files[0];

      this.formGroup.patchValue({
        photo: file?.name,
      });

      if (!Utility.checkValidImageExtension(file?.name || 'file.invalid')) {
        this.formGroup.controls.photo.setErrors({ invalidImage: true });
        return;
      }

      this.file = {
        data: file,
        state: 'in',
        inProgress: false,
        progress: 0,
        canRetry: false,
        canCancel: true,
      };
      this.startUploadProcess(file);
    };
    fileUpload.click();
  }

  /**
   * @description this method call API to get presigned URL
   * @param { File } file
   */
  startUploadProcess(file: File) {
    if (Utility.checkValidImageSize(file?.size, 25)) {
      this.formGroup?.controls?.photo?.setErrors({ invalidImageSize: true });
      return;
    }
    const fileType = file?.type;
    this.buildingManagementService
      .getSiteBucketPresignedURL(fileType)
      .subscribe({
        next: (res: any) => {
          this.uploadFile(res, file);
        },
        error: (error: Error) => {},
      });
  }

  /**
   * @description this method uploads file by adding all necessary fields to form data and
   *              calls API to upload
   * @param { any } preSignedURLData
   * @param { File } file
   */
  uploadFile(preSignedURLData: any, file: File) {
    let { fields } = preSignedURLData;
    this.buildingPhotoKey = fields['key'];
    var fd = new FormData();
    let fieldsKeys = Object.keys(fields);
    for (const field of fieldsKeys) {
      fd.append(field, fields[field]);
    }

    fd.append('file', file);
    // fd.set("Content-Type", file.type);

    this.buildingManagementService
      .uploadFile(preSignedURLData.url, fd)
      .subscribe(
        (res: any) => {},
        (err: Error) => {}
      );
  }

  /**
   * @description this method call when new building is added or existing is updated
   * @param { boolean } isDone
   */
  onUpdateBuildingHandler(isDone: boolean) {
    let siteID = this.site?.siteId;
    let buildingName = this.formGroup?.controls?.name?.value;
    let buildingAddress = this.formGroup?.controls?.address?.value;
    this.buildingManagementService
      .addNewBuildingBySiteId(buildingAddress, buildingName, siteID)
      .subscribe({
        next: (res: any) => {
          if (isDone) {
            this.resetNavigation();
            this.dialogRef.close(this.result);
          }
        },
        error: (err: Error) => {
          if (window.__dev__) console.log('addNewBuildingBySiteId', err);
          this.notificationService.showError(err?.message);
        },
      });
  }

  /**
   * @description this function will add/updated building and navidate to add floor form
   * @returns { void }
   */
  openAddFloor() {
    let buildingId = this.sharedData?.selectedBuilding?.buildingId;
    let siteID = this.sharedData.selectedSite?.siteId;
    let buildingName = this.formGroup?.controls?.name?.value;
    let buildingAddress = this.formGroup?.controls?.address?.value;
    if (!this.validateInput(buildingName, buildingAddress)) {
      return;
    }
    if (!this.sharedData.editMode.building) {
      this.buildingManagementService
        .addNewBuildingBySiteId(buildingAddress, buildingName, siteID)
        .subscribe({
          next: (res: any) => {
            this.buildingId = res.buildingId;
            this.sharedDataService.setEditMode({
              building: true,
              floor: false,
            });
            this.sharedDataService.setSelectedBuilding({
              buildingName: this.formGroup?.controls?.name?.value,
              buildingId: this.buildingId,
            });
            this.navigateToAddFloor();
            this.notificationService.showSuccess(
              this.commonService.stringInterPolation(
                this.translate.instant('SUCCESSFULLY_CREATED'),
                this.translate.instant('BUILDING')
              )
            );
          },
          error: (err: Error) => {
            if (window.__dev__) console.log('addNewBuildingBySiteId', err);
            this.notificationService.showError(err?.message);
          },
        });
    } else {
      // building will be updated if data is modified
      let currentData: IBuilding = {
        buildingId: this.previousData?.buildingId || '',
        buildingName: buildingName,
        address: buildingAddress,
      };

      if (
        this.previousData?.buildingName !== currentData?.buildingName ||
        this.previousData?.address !== currentData?.address
      ) {
        this.buildingManagementService
          .updateBuilding(
            this.selectedSite?.siteId,
            this.previousData?.buildingId || '',
            buildingName,
            buildingAddress
          )
          .subscribe({
            next: (building: IBuilding | undefined) => {
              this.sharedDataService.setSelectedBuilding({
                buildingId: buildingId,
                buildingName: buildingName,
              });
              this.navigateToAddFloor();
              this.notificationService.showSuccess(
                this.translate.instant('SUCCESSFULLY_UPDATED')
              );
            },
            error: (err: Error) => {
              if (window.__dev__) console.log('updateBuilding', err);
              this.notificationService.showError(err?.message);
            },
          });
      }
    }
    if (this.sharedData.editMode.building) {
      this.navigateToAddFloor();
    }
  }

  /**
   * @description this function will add/update building and navigate to close the modal
   * @returns { void }
   */
  onDoneHandler() {
    let siteID = this.sharedData.selectedSite?.siteId;
    let buildingName = this.formGroup?.controls?.name?.value;
    let buildingAddress = this.formGroup?.controls?.address?.value;
    let buildingImg = this.buildingPhotoKey ?? this.previousData?.buildingImg;
    let buildingImgName =
      this.file?.data?.name ?? this.previousData?.buildingImgName;
    if (!this.validateInput(buildingName, buildingAddress)) {
      return;
    }
    if (this?.sharedData?.editMode?.building) {
      let currentData: IBuilding = {
        buildingId: this.previousData?.buildingId || '',
        buildingName: buildingName,
        buildingImgName: buildingImgName,
        address: buildingAddress,
        buildingImg: buildingImg,
      };

      if (
        !this.previousData?.buildingId ||
        Utility.compareJson(this.previousData || {}, currentData || {})
      ) {
        this.resetNavigation();
        this.dialogRef.close(this.result);
        return;
      }

      this.buildingManagementService
        .updateBuilding(
          this.selectedSite?.siteId,
          this.previousData?.buildingId,
          buildingName,
          buildingAddress
        )
        .subscribe({
          next: (building: IBuilding | undefined) => {
            this.sharedDataService.setEditMode({
              building: false,
              floor: false,
            });

            this.sharedDataService.setSelectedBuilding({
              buildingName: building?.buildingName,
              buildingId: building?.buildingId,
            });
            // here you can also call navigateBack for going back and closeDialog for closing current dialog
            this.resetNavigation();
            this.dialogRef.close(this.result);
            this.notificationService.showSuccess(
              this.translate.instant('SUCCESSFULLY_UPDATED')
            );
          },
          error: (err: Error) => {
            this.sharedDataService.setEditMode({
              building: true,
              floor: false,
            });
            if (window.__dev__) console.log('updateBuilding', err);
            this.notificationService.showError(err?.message);
          },
        });
    } else {
      this.buildingManagementService
        .addNewBuildingBySiteId(buildingAddress, buildingName, siteID)
        .subscribe({
          next: (res: any) => {
            this.buildingId = res.buildingId;
            this.sharedDataService.setSelectedBuilding({
              buildingName: this.formGroup?.controls?.name?.value,
              buildingId: this.buildingId,
            });
            this.notificationService.showSuccess(
              this.commonService.stringInterPolation(
                this.translate.instant('SUCCESSFULLY_CREATED'),
                this.translate.instant('BUILDING')
              )
            );
            this.navigateToEditSite();
          },
          error: (err: Error) => {
            if (window.__dev__) console.log('addNewBuildingBySiteId', err);
            this.notificationService.showError(err?.message);
          },
        });
    }
  }

  /**
   * @description this method will navigate user to the add floor form so that
   *              user can remove floor.
   * @param { any } floorId id of floor to remove
   * @param { any } floorName name of floor to remove
   */
  removeFloor(floorId: any, floorName: any) {
    let buildingName = this.formGroup?.controls?.name?.value;
    let buildingAddress = this.formGroup?.controls?.address?.value;

    if (
      this.previousData?.buildingName !== buildingName ||
      this.previousData?.address !== buildingAddress
    ) {
      this.buildingManagementService
        .updateBuilding(
          this.selectedSite?.siteId,
          this.previousData?.buildingId || '',
          buildingName,
          buildingAddress
        )
        .subscribe({
          next: (building: IBuilding | undefined) => {
            this.notificationService.showSuccess(
              this.translate.instant('SUCCESSFULLY_UPDATED')
            );
          },
          error: (err: Error) => {
            if (window.__dev__) console.log('updateBuilding', err);
            this.notificationService.showError(err?.message);
          },
        });
    }

    this.sharedDataService.setSelectedFloor({
      floorId: floorId,
      floorName: floorName,
    });
    this.sharedDataService.setEditMode({
      building: true,
      floor: true,
    });
    this.navigateToAddFloor();
  }

  /**
   * @description this method validates input and set errors accordingly
   * @param { any } buildingName
   * @param { any } buildingAddress
   * @param { any } buildingImg
   */
  validateInput(buildingName: any, buildingAddress: any) {
    let result = true;
    if (buildingName === null || buildingName === undefined) {
      this.formGroup?.controls?.name?.setErrors({ required: true });
      result = false;
    }
    if (buildingAddress === null || buildingAddress === undefined) {
      this.formGroup?.controls?.address?.setErrors({ required: true });
      result = false;
    }
    this.changeDetectorRef.detectChanges();
    return result;
  }

  /**
   * @description this method is called when Remove button is clicked.
   *              this method calls delete floor API to remove floor from the building
   */
  onRemoveBuilding() {
    if (this.floors?.length > 0) {
      this.helperService.openModalDialog(
        RestrictRemoveDialogComponent,
        {
          title: this.translate.instant('DELETE_BUILDING'),
          subTitle: this.commonService.stringInterPolation(
            this.translate.instant('UNABLE_TO_REMOVE_BUILDING'),
            this.sharedData?.selectedBuilding?.buildingName
          ),
          bodyText: this.commonService.stringInterPolation(
            this.translate.instant('RESTRICT_REMOVE_BUILDING'),
            this.sharedData?.selectedBuilding?.buildingName
          ),
        },
        CONSTANTS.FIXED_SCREEN_MODAL
      );
    } else {
      const dialogInstance = this.helperService.openModalDialog(
        ConfirmRemoveDialogComponent,
        {
          title: this.translate.instant('REMOVE_BUILDING'),
          bodyText: this.translate.instant(
            'ARE_YOU_SURE_YOU_WANT_TO_REMOVE_BUILDING'
          ),
          footerText: this.translate.instant(
            'BY_MOVING_FORWARD_YOU_WILL_LOSE_ALL_INFO_BUILDING'
          ),
        },
        CONSTANTS.FIXED_SCREEN_MODAL
      );
      dialogInstance.afterClosed().subscribe((result: boolean) => {
        this.closeConfirmDialog();
        if (result) {
          this.buildingManagementService
            .deleteBuilding(this.sharedData?.selectedBuilding?.buildingId)
            .subscribe({
              next: (res: any) => {
                if (
                  res?.buildingId ==
                  this?.sharedData?.selectedBuilding?.buildingId
                ) {
                  this.sharedDataService?.setSelectedBuilding({
                    buildingId: null,
                    buildingName: null,
                  });
                  this.sharedDataService?.setEditMode({
                    building: false,
                    floor: false,
                  });
                  this.result = true;
                  this.notificationService.showSuccess(
                    this.commonService.stringInterPolation(
                      this.translate.instant('SUCCESSFULLY_DELETED'),
                      this.translate.instant('BUILDING')
                    )
                  );
                  const buildings = this.stateManagementService.getBuildings();
                  if (
                    buildings?.length !== undefined &&
                    buildings.length <= 0
                  ) {
                    this.buildingManagementService.buildingsView = false;
                  } else this.buildingManagementService.buildingsView = true;
                  this.navigateToEditSite(this.result);
                }
              },
              error: (err: Error) => {
                this.notificationService.showError(err?.message);
              },
            });
        }
      });
    }
  }

  validateEmpty(buildingName: any, buildingAddress: any, buildingImg: any) {
    if (
      (buildingName === null || buildingName === undefined) &&
      (buildingAddress === null || buildingAddress === undefined) &&
      (buildingImg === null || buildingImg === undefined)
    )
      return true;
    return false;
  }

  /**
   * @description downloads building image
   */
  photoInputClick() {
    const fileDownloader = document.getElementById(
      'building-photo-download'
    ) as HTMLInputElement;
    fileDownloader.click();
  }

  getFloorBySiteBuildingId(siteId: string, buildingId: string) {
    this.floorManagementService
      .getAllFloorsByBuildingId(siteId, buildingId)
      .pipe(
        mergeMap((floors: Floor) => {
          this.floors = floors;
          let floorObservables = floors?.map((floor: IFloor) =>
            this.floorManagementService.getUnitsAndAccessPointsByFloor(
              siteId,
              buildingId,
              floor?.floorId
            )
          );
          return forkJoin(floorObservables);
        })
      )
      .subscribe({
        error: (err: any) => {
          if (window.__dev__)
            console.log(
              'getAllFloorsByBuildingId from add building details',
              err
            );
          this.errorMessage = err?.message;
        },
      });
  }
}
